// PR-8 — Mobil pull-to-refresh hook'u (paylaşılan, minimal).
//
// TOUCH-ONLY: yalnız touchstart/touchmove/touchend dinler → desktop (mouse)
// davranışı yapısal olarak DEĞİŞMEZ. Kural seti:
//   - Yalnız scrollTop === 0 iken başlayan aşağı-çekişler sayılır.
//   - Sönümleme: gösterilen mesafe = ham mesafe / 2 (doğal his).
//   - THRESHOLD (70px) geçilmeden bırakılırsa → hiçbir şey tetiklenmez.
//   - Tek-uçuş: refresh sürerken ikinci pull tetiklenemez (ref guard).
//   - onRefresh fire-and-forget olabilir; gösterge MIN_SPIN_MS görünür kalır
//     (canlı listener'lar içeriği zaten kendisi tazeler).
//
// Kullanım:
//   const ptr = usePullToRefresh({ enabled, targetRef, onRefresh });
//   // targetRef.current yoksa window/document scroll'u kullanılır.
//   // render: ptr.visible ? <indicator style={{height: ptr.pullPx}} .../> : null
import { useCallback, useEffect, useRef, useState } from "react";

const THRESHOLD_PX = 70;
const MAX_PULL_PX = 110;
const MIN_SPIN_MS = 700;

type Opts = {
  enabled: boolean;
  // Scroll container elementi; null/verilmezse window scroll'u (document) izlenir.
  targetRef?: { current: HTMLElement | null } | null;
  onRefresh: () => void | Promise<void>;
};

export default function usePullToRefresh({ enabled, targetRef = null, onRefresh }: Opts) {
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const getScrollTop = useCallback(() => {
    const el = targetRef?.current;
    if (el) return el.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }, [targetRef]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el: HTMLElement | Document = targetRef?.current || document;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (getScrollTop() > 0) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || refreshingRef.current) return;
      // Kullanıcı bu arada aşağı scroll'a geçtiyse pull iptal.
      if (getScrollTop() > 0) {
        pullingRef.current = false;
        setPullPx(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        setPullPx(0);
        return;
      }
      setPullPx(Math.min(MAX_PULL_PX, dy / 2)); // sönümleme
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      setPullPx((cur) => {
        if (cur >= THRESHOLD_PX && !refreshingRef.current) {
          refreshingRef.current = true;
          setRefreshing(true);
          const started = Date.now();
          Promise.resolve()
            .then(() => onRefreshRef.current())
            .catch(() => {})
            .then(() => {
              const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - started));
              setTimeout(() => {
                refreshingRef.current = false;
                setRefreshing(false);
              }, wait);
            });
        }
        return 0;
      });
    };

    // passive:true — scroll'u ASLA bloklamayız (indicator transform'la çizilir,
    // preventDefault gerekmez → iOS/Android native scroll hissi korunur).
    el.addEventListener("touchstart", onTouchStart as any, { passive: true });
    el.addEventListener("touchmove", onTouchMove as any, { passive: true });
    el.addEventListener("touchend", onTouchEnd as any, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd as any, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart as any);
      el.removeEventListener("touchmove", onTouchMove as any);
      el.removeEventListener("touchend", onTouchEnd as any);
      el.removeEventListener("touchcancel", onTouchEnd as any);
    };
  }, [enabled, targetRef, getScrollTop]);

  return {
    pullPx,
    refreshing,
    armed: pullPx >= THRESHOLD_PX,
    visible: pullPx > 6 || refreshing,
  };
}
